import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "tss-react/mui";

import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Checkbox from "@mui/material/Checkbox";

import Scheduler from "../utils/Scheduler";
import NextJobForm from "../utils/NextJobForm";
import { showToast } from "../../../../store/actions";
import { useCronReducer } from "../../../../util/cron";
import WorkspaceService from "../../../../service/workspace";
import { ButtonUI, ConfirmPopup } from "../../../Common";
import { FormTile } from "../../../Common/Tile";
import { regexName } from "../../../../util/regularExpress";
import CommonLayout from "../../../Common/Layout";
import KMKGroupSelect from "../../../Common/KMKGroupSelect";
import ComponentSuspense from "../../../Common/ComponentSuspense";
import HelpContextIcon from "../../../Common/HelpPanel/HelpContextIcon";
import { DETAUL_CRON_VALUE, JOB_BY_TYPE } from "../utils/JobData";
import RunJobAsComponent from "../utils/RunJobAsComponent";
import { navScheduledJobs } from "../../../kmk/AppData";

import { arrangeSchema, checkIsClusterExist, checkIsSchemaExist, setPrevState } from "../../../../util/common";

import DataPipelineService from "../../../../service/dataPipelineService";
import SmallToolbar from "../../../Common/SmallToolbar";
import CloneJob from "../CloneJob";

const MyAceEditor = React.lazy(() => import("../../../Common/AceEditor"));

const useStyles = makeStyles()((theme) => ({
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 0,
    paddingRight: "20px",
    display: "inline-block",
  },
  BigTileContainer: {
    paddingTop: "0px !important",
    marginTop: "10px !important",
  },
  subContainer: {
    display: "flex",
    padding: 0,
    borderTop: 0,
    width: "100%",
    flexDirection: "column",
  },
  PanelContainer: {
    padding: "22px 30px 30px 30px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "250px",
    marginLeft: "inherit",
    "& .MuiPaper-root": {
      height: "auto",
    },
  },
  queryArea: {
    width: "100% !important",
    height: "173px !important",
    border: "1px solid",
    fontSize: "0.7rem",
    "&::selection": { background: "#B9B9B9", color: "#000000" },
    "&::-moz-selection": { background: "#B9B9B9", color: "#000000" },
    "&::-webkit-selection": { background: "#B9B9B9", color: "#000000" },
  },
  AutoComplete: {
    "& .MuiAutocomplete-listbox": {
      maxWidth: "100%",
      wordBreak: "break-all !important",
    },
  },
}));

const CustomPaper = React.memo((props) => {
  return (
    <Paper
      {...props}
      sx={{
        "& .MuiAutocomplete-listbox": {
          maxWidth: "100%",
          wordBreak: "break-all !important",
        },
      }}
    />
  );
});

const Types = [{ value: "SQL", text: "SQL" }];

const compareQuery = (oldQuery, newQuery) => {
  oldQuery = oldQuery.trim().toLowerCase();
  newQuery = newQuery.trim().toLowerCase();
  return oldQuery !== newQuery;
};

export default function RoutingScheduler() {
  const { Workspace_id, Job_id } = useParams();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector((state) => state.user.currentUser);
  const workspace = useSelector((state) => state.workspace);
  const config = useSelector((state) => state.config);
  const clusterList = useSelector((state) => state.clusterList);
  const { defaultSchema, defaultCluster } = config;
  const { Name: workspaceName, accessRole, DBName } = workspace.workspace;

  const [locations, setLocations] = useState([]);

  // For Schedule Panel
  const [values, dispatchValues] = useCronReducer("");

  const [jobState, setJobState] = useState({
    loading: false,
    jobName: "",
    DataPipeline_id: "",
    Type: "SQL",
    query: "",
    queryError: "",
    timeZone: "",
    nextRunTime: "",
    scheduleEnabled: "",
    runJobAsUserId: "",
    runJobAsUserFullName: "",
    queryLastUpdatedByName: "",
    database: "",
    schema: "",
    location: "SNOWFLAKE",
    isEntryJob: false,
  });

  const [dataPipelineList, setDataPipelineList] = useState([]);
  // Adding only query for now as it is the only property of which we are require previous state
  const [prevJobState, setPrevJobState] = useState({
    runJobAsUserId: "",
    query: "",
  });

  const [participantsExcludeVisitor, setParticipantsExcludeVisitor] = useState([]);
  const [userNotExist, setUserNotExist] = useState(false);

  const [deleteJobState, setDeleteJobState] = useState({
    openConfirm: false,
    confirmMessage: "",
    loadingForConfirm: false,
  });

  const [jobReassignment, setJobReassignment] = useState({
    openConfirm: false,
    confirmMessage: "",
    loadingForConfirm: false,
  });

  const [extSnowflakeClusterList, setExtSnowflakeClusterList] = useState([]);
  const [computeState, setComputeState] = useState({
    cluster: defaultCluster,
  });

  const [nextJobState, setNextJobState] = useState({
    nextJobToggle: false,
    nextDataPipelineId: "",
    nextJobId: "",
    activeSchedule: "",
    errorDataPipelineId: "",
    errorJobId: "",
    errorSchedule: "",
    alternateDataPipelineId: "",
    alternateJobId: "",
    alternateJobSchedule: "",
  });

  const [openClone, setOpenClone] = useState(false);
  const [schemas, setSchemas] = useState([]);
  const [schemaError, setSchemaError] = useState(false);

  const queryRef = useRef(null);
  const textRef = useRef(null);

  const currentUserFullName = useMemo(() => `${user.FirstName} ${user.LastName}`, [user.FirstName, user.LastName]);

  const locationType = useMemo(
    () => locations.find(({ value }) => value === jobState.location)?.type || "",
    [locations, jobState.location]
  );

  const connectionClusters = useMemo(() => {
    if (locationType === "snowflake") {
      if (jobState.location === "SNOWFLAKE") return clusterList;
      return extSnowflakeClusterList;
    }
    return [];
  }, [clusterList, extSnowflakeClusterList, locationType, jobState.location]);

  const disableSaveBtn = useMemo(() => {
    if (jobState.loading) return true;
    if (Job_id && jobState.location === "SNOWFLAKE" && userNotExist) return true;
    if (jobState.jobName === "") return true;
    if (!jobState.Type) return true;
    if (jobState.query === "") return true;
    if (jobState.queryError) return true;
    if (locationType === "snowflake") {
      if (!computeState.cluster) return true;
      if (jobState.location === "SNOWFLAKE" && jobState.runJobAsUserId === "") return true;
      if (jobState.database === "" || jobState.schema === "") return true;
    }

    return (
      nextJobState.nextJobToggle === true &&
      (!(nextJobState.nextJobId || nextJobState.errorJobId || nextJobState.alternateJobId) ||
        (nextJobState.nextJobId && nextJobState.nextJobId === nextJobState.alternateJobId))
    );
  }, [
    Job_id,
    locationType,
    nextJobState.nextJobId,
    nextJobState.errorJobId,
    nextJobState.alternateJobId,
    nextJobState.nextJobToggle,
    jobState.Type,
    computeState.cluster,
    jobState.database,
    jobState.jobName,
    jobState.loading,
    jobState.location,
    jobState.query,
    jobState.queryError,
    jobState.runJobAsUserId,
    jobState.schema,
    userNotExist,
  ]);

  const handleCancel = (setState, stateVar) => {
    setPrevState(setState, { [stateVar]: false });
  };

  const deleteRoutingJob = async () => {
    setPrevState(setDeleteJobState, { loadingForConfirm: true });

    const wsService = new WorkspaceService();
    const result = await wsService.deleteRoutingJob(Workspace_id, Job_id);

    if (result.success) {
      dispatch(
        showToast({
          message: result.message,
          type: "success",
        })
      );
      return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    }
    setPrevState(setDeleteJobState, { openConfirm: false, loadingForConfirm: false });
  };

  // to get config for save based on location for mysql, external snowflake and current workspace
  const getLocationConfig = useCallback(() => {
    // if location type is mysql
    if (locationType !== "snowflake") return {};

    const baseConfig = {
      cluster: computeState.cluster,
      database: jobState.database,
      schema: jobState.schema,
    };

    // if location type is external snowflake
    if (jobState.location !== "SNOWFLAKE") return baseConfig;

    const userConfig =
      accessRole === 1 || !compareQuery(prevJobState.query, jobState.query)
        ? {
            runJobAsUserId: jobState.runJobAsUserId,
            runJobAsUserFullName: jobState.runJobAsUserFullName,
          }
        : {
            runJobAsUserId: user.User_id,
            runJobAsUserFullName: currentUserFullName,
          };

    // if location type is current workspace snowflake
    return { ...baseConfig, ...userConfig };
  }, [
    accessRole,
    computeState.cluster,
    currentUserFullName,
    locationType,
    prevJobState.query,
    jobState.database,
    jobState.location,
    jobState.query,
    jobState.runJobAsUserFullName,
    jobState.runJobAsUserId,
    jobState.schema,
    user.User_id,
  ]);

  const handleSave = async (e, clone = false) => {
    // If popup is open then just close it first
    if (jobReassignment.openConfirm) handleCancel(setJobReassignment, "openConfirm");

    setPrevState(setJobState, { loading: true });

    let response = null;
    const submitData = {
      name: jobState.jobName.trim(),
      Type: jobState.Type,
      location: jobState.location,
      DataPipeline_id: jobState.DataPipeline_id,
      query: jobState.query,
      schedule: values.cronValue || DETAUL_CRON_VALUE,
      scheduleEnabled: jobState.scheduleEnabled || false,
      enableNextJob: nextJobState.nextJobToggle,
      ...(nextJobState.nextJobToggle
        ? {
            nextPipeline: nextJobState.nextDataPipelineId,
            nextJob: nextJobState.nextJobId,
            errorPipeline: nextJobState.errorDataPipelineId,
            errorJob: nextJobState.errorJobId,
            disableNextJob: nextJobState.activeSchedule,
            disableErrorJob: nextJobState.errorSchedule,
            alternatePipeline: nextJobState.alternateDataPipelineId,
            alternateJob: nextJobState.alternateJobId,
            disableAlternateJob: nextJobState.alternateJobSchedule,
          }
        : {
            nextPipeline: null,
            nextJob: null,
            errorPipeline: null,
            errorJob: null,
            disableNextJob: null,
            disableErrorJob: null,
            alternatePipeline: null,
            alternateJob: null,
            disableAlternateJob: null,
          }),
      ...getLocationConfig(),
      queryLastUpdatedByName: !Job_id
        ? currentUserFullName
        : compareQuery(prevJobState.query, jobState.query)
        ? currentUserFullName
        : jobState.queryLastUpdatedByName,
      isEntryJob: jobState.isEntryJob,
    };

    const wsService = new WorkspaceService();
    if (!Job_id) response = await wsService.createRoutingJob(submitData, Workspace_id);
    else response = await wsService.updateRoutingJob(submitData, Workspace_id, Job_id);

    if (response.success) {
      if (clone) {
        setPrevState(setJobState, { loading: false });
        return true;
      }

      dispatch(showToast({ message: response.message, type: "success" }));
      return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    }

    setPrevState(setJobState, { loading: false });
  };

  const handleCancelClick = useCallback(() => {
    navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
  }, [Workspace_id, navigate]);

  const handleSchemaChange = (e, value) => {
    setPrevState(setJobState, { schema: value?.title || "", database: value?.groupBy || "" });
    setSchemaError((prevState) => (prevState ? !prevState : prevState));
  };

  const handleUserChange = useCallback(
    (e, { runJobAsUserId, runJobAsUserFullName }) => {
      setPrevState(setJobState, { runJobAsUserId, runJobAsUserFullName });
      setUserNotExist(false);
    },
    [setUserNotExist]
  );

  const getSchemaList = useCallback(
    async (location = "SNOWFLAKE") => {
      const wsService = new WorkspaceService();
      let response;

      if (location === "SNOWFLAKE") {
        response = await wsService.getSchemaListByUser();
      } else {
        response = await wsService.getExternalSnowflakeSchemas(Workspace_id, location, {
          schemasOnly: true,
        });
      }

      const { success, data } = response;

      if (!success) return {};
      const schemas = arrangeSchema(data, setSchemas);
      return schemas;
    },
    [Workspace_id]
  );

  const getExternalSnowflakeCluster = useCallback(
    async (location) => {
      if (!location || location === "SNOWFLAKE") return [];
      const wsService = new WorkspaceService();
      const { success, data } = await wsService.getExternalSnowflakeCluster(Workspace_id, location);
      if (success && data?.length) {
        const clusters = data?.map((cluster) => ({ value: cluster?.name || "" }));
        setExtSnowflakeClusterList(clusters);
        return clusters;
      }
      setExtSnowflakeClusterList([]);
      return [];
    },
    [Workspace_id]
  );

  const handleConnectionChange = useCallback(
    async (location) => {
      setPrevState(setJobState, { loading: true, location });

      const locationType = locations?.find(({ value }) => value === location)?.type || "";

      if (locationType === "snowflake") {
        const [schemasByDB, clusters] = await Promise.all([
          getSchemaList(location),
          location !== "SNOWFLAKE" ? getExternalSnowflakeCluster(location) : clusterList,
        ]);

        const isSchemaExist = checkIsSchemaExist(schemasByDB, jobState.database, jobState.schema);

        if (!isSchemaExist)
          setPrevState(setJobState, {
            ...(location !== "SNOWFLAKE" ? { database: "", schema: "" } : { database: DBName, schema: defaultSchema }),
          });

        const isClusterExist = checkIsClusterExist(clusters, computeState.cluster);

        setPrevState(setComputeState, {
          cluster: isClusterExist ? computeState.cluster : location === "SNOWFLAKE" ? defaultCluster : "",
        });
      }

      setPrevState(setJobState, { loading: false });
    },
    [
      DBName,
      clusterList,
      computeState.cluster,
      defaultCluster,
      defaultSchema,
      getExternalSnowflakeCluster,
      getSchemaList,
      jobState.database,
      locations,
      jobState.schema,
    ]
  );

  useEffect(() => {
    if (![1, 2].includes(accessRole)) {
      dispatch(
        showToast({
          message: "You do not have permission to perform this action!",
          type: "error",
        })
      );
      return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    }
  }, [Workspace_id, accessRole, dispatch, navigate]);

  useEffect(() => {
    //Get Location List for "Connection" Dropdown
    const getLocationList = async () => {
      const wsService = new WorkspaceService();
      const locations = await wsService.getLocationList(Workspace_id);

      return locations
        .filter((item) => ["mysql", "snowflake"].includes(item.Detail.type.toLowerCase()))
        .map(({ Stage_id, Name, Detail }) => ({ value: Stage_id, text: Name, type: Detail?.type || "" }));
    };

    const getRoutingSchedule = async (locations) => {
      const wsService = new WorkspaceService();
      const response = await wsService.getRoutingSchedule(Workspace_id, Job_id);
      if (response === "Unauthorized") return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);

      if (response.success) {
        dispatchValues({
          type: "set_values",
          value: response.data.Schedule,
        });

        setPrevState(setNextJobState, {
          nextDataPipelineId: response.data.NextPipeline_id || "",
          nextJobId: response.data.NextJob_id || "",
          errorDataPipelineId: response.data.ErrorPipeline_id || "",
          errorJobId: response.data.ErrorJob_id || "",
          alternateDataPipelineId: response.data.AlternatePipeline_id || "",
          alternateJobId: response.data.AlternateJob_id || "",
          nextJobToggle: response.data.enableNextJob || false,
        });

        setPrevState(setPrevJobState, {
          runJobAsUserId: response.data.Detail.runJobAsUserId,
          query: response.data.Detail.query,
        });

        const location = response.data.Detail.location;
        const selectedLocation = locations.find(({ value }) => value === location);
        const isLocationSnowflake = selectedLocation?.type === "snowflake";
        const clusters = await (isLocationSnowflake
          ? getExternalSnowflakeCluster(selectedLocation?.value)
          : clusterList);
        const isClusterExist = checkIsClusterExist(clusters || [], response.data?.Detail?.Cluster || "");

        setPrevState(setComputeState, {
          cluster: isClusterExist ? response.data.Detail.Cluster : isLocationSnowflake ? defaultCluster : "",
        });

        return {
          jobName: response.data.Name,
          Type: response.data.Detail.Type || "SQL",
          location: selectedLocation?.value || "SNOWFLAKE",
          DataPipeline_id: response.data.DataPipeline_id,
          query: response.data.Detail.query,
          timeZone: response.data.ServerTimeZone,
          scheduleEnabled: response.data.Enabled,
          nextRunTime: response.data.nextRun,
          runJobAsUserId: response.data.Detail.runJobAsUserId,
          runJobAsUserFullName: response.data.Detail.runJobAsUserFullName,
          queryLastUpdatedByName: response.data.Detail.queryLastUpdatedByName,
          database: response.data.Detail.database,
          schema: response.data.Detail.schema,
        };
      } else return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    };
    const getWorkspaceParticipants = async () => {
      const wsService = new WorkspaceService();
      const { data, success } = await wsService.getWorkspaceParticipants(Workspace_id, [1, 2]);
      return success ? data : [];
    };

    const getTimezone = async () => {
      const wsService = new WorkspaceService();
      const result = await wsService.getTimezone(Workspace_id);
      return result.data;
    };

    const getDataPipelineList = async () => {
      const pipeline = new DataPipelineService();
      const response = await pipeline.getDataPipelineList(Workspace_id);
      if (response.success && response.data?.dataPipelineList?.length) return response.data.dataPipelineList;
      return [];
    };

    async function fetchData() {
      if (clusterList === false) return;
      setPrevState(setJobState, { loading: true });

      let initialState = {};

      const [locations, dataPipelines, workspaceParticipatns] = await Promise.all([
        getLocationList(),
        getDataPipelineList(),
        getWorkspaceParticipants(),
      ]);

      // no need to await this fetched schema list is for create job and will not be of use in this fun
      if (!Job_id) getSchemaList();

      setParticipantsExcludeVisitor(workspaceParticipatns);

      // Adding entry for current workspace option
      const snowflakeLocation = {
        value: "SNOWFLAKE",
        text: workspaceName + " (Current Workspace DB)",
        type: "snowflake",
      };

      setLocations([snowflakeLocation, ...locations]);

      if (Job_id) {
        initialState = await getRoutingSchedule(locations);

        const selectedLocationType =
          locations.find(({ value }) => value === initialState?.location)?.type || "snowflake";

        if (selectedLocationType === "snowflake") {
          const schemasByDB = await getSchemaList(initialState.location);
          const isSchemaExist = checkIsSchemaExist(schemasByDB, initialState.database, initialState.schema);
          if (!isSchemaExist) {
            setSchemaError(true);
            initialState.database = "";
            initialState.schema = "";
          }

          if (initialState.location === "SNOWFLAKE")
            setUserNotExist(
              !workspaceParticipatns.some((_user) => {
                return _user.User_id === initialState.runJobAsUserId;
              })
            );
        }
      } else {
        initialState.timeZone = await getTimezone();
        initialState.runJobAsUserId = user.User_id;
        initialState.runJobAsUserFullName = currentUserFullName;
        initialState.database = DBName;
        initialState.schema = defaultSchema;
      }

      if (!initialState.DataPipeline_id)
        initialState.DataPipeline_id = dataPipelines?.find((pipeline) => pipeline.IsDefault === 1)?.DataPipeline_id;
      setDataPipelineList(dataPipelines);

      if (!initialState.runJobAsUserId) {
        initialState.runJobAsUserId = user.User_id;
        initialState.runJobAsUserFullName = currentUserFullName;
      }

      let isEntryJob = false;
      if (Job_id) {
        const currentPipeline = dataPipelines.find(
          ({ DataPipeline_id }) => parseInt(DataPipeline_id) === parseInt(initialState.DataPipeline_id)
        );
        isEntryJob = currentPipeline.EntryJob_id === parseInt(Job_id);
      }

      setPrevState(setJobState, {
        ...initialState,
        loading: false,
        isEntryJob,
      });
    }
    fetchData();
  }, [
    DBName,
    Job_id,
    Workspace_id,
    clusterList,
    currentUserFullName,
    defaultCluster,
    defaultSchema,
    dispatchValues,
    getExternalSnowflakeCluster,
    getSchemaList,
    navigate,
    user.User_id,
    workspaceName,
  ]);

  /**
   * Alpha Numeric Characters only with Space allow (first letter should Alpha)
   * @param {*} str
   * @returns
   */
  const handleJobName = (str, dataState) => {
    const isValid = regexName(str, "jobName", "_ -");
    if (isValid) setPrevState(setJobState, { [dataState]: str });
  };

  const resetNextJob = () =>
    setPrevState(setNextJobState, {
      activeSchedule: "",
      nextJobToggle: false,
      nextJobId: "",
      errorJobId: "",
      alternateJobId: "",
      errorSchedule: "",
      alternateJobSchedule: "",
    });

  const confirmPipelineChange = useCallback((e) => {
    setJobState((prev) => ({
      ...prev,
      DataPipeline_id: e.target.value,
      ...(prev.DataPipeline_id ? { isEntryJob: false } : {}),
    }));
    resetNextJob();
  }, []);

  const addJobForm = () => {
    return (
      <Grid container>
        <Grid container direction={"row"} spacing={2} sx={{ mb: "16px" }}>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 4,
            }}
          >
            <TextField
              value={jobState.jobName}
              fullWidth
              required
              label="Job Name"
              inputProps={{ maxLength: 30 }}
              onChange={(e) => handleJobName(e.target.value, "jobName")}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 4,
            }}
          >
            <FormControl fullWidth required>
              <InputLabel>Pipeline</InputLabel>
              <Select
                value={jobState.DataPipeline_id}
                onChange={confirmPipelineChange}
                label="Pipeline"
                disabled={!!Job_id}
                MenuProps={{ PaperProps: { style: { maxHeight: "300px", maxWidth: "300px" } } }}
                renderValue={(selected) => {
                  const selectedPipeline = dataPipelineList.find((p) => p.DataPipeline_id === selected);
                  const isOverflow = (textRef.current?.scrollWidth > textRef.current?.clientWidth || false) && !!Job_id;

                  return (
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Tooltip
                        placement="bottom"
                        title={selectedPipeline?.Name}
                        arrow
                        disableHoverListener={!isOverflow}
                      >
                        <Typography
                          variant="body1"
                          ref={textRef}
                          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "300px" }}
                        >
                          {selectedPipeline?.Name || "Select a pipeline"}
                        </Typography>
                      </Tooltip>
                      {selectedPipeline?.IsDefault === 1 && (
                        <Tooltip placement="right" title="Default Pipeline" arrow>
                          <StarBorderOutlinedIcon sx={{ fontSize: "22px" }} color="info" />
                        </Tooltip>
                      )}
                    </Stack>
                  );
                }}
              >
                {dataPipelineList.map((p) => (
                  <MenuItem
                    key={p.DataPipeline_id}
                    value={p.DataPipeline_id}
                    style={{ whiteSpace: "normal", wordWrap: "break-word", wordBreak: "break-all" }}
                  >
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography variant="body1" sx={{ m: 0, p: 0 }}>
                        {p.Name}
                      </Typography>
                      {p.IsDefault === 1 && (
                        <Tooltip placement="right" title="Default Pipeline" arrow>
                          <StarBorderOutlinedIcon sx={{ fontSize: "22px" }} color="info" />
                        </Tooltip>
                      )}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 4,
            }}
          >
            <FormGroup>
              <FormControlLabel
                control={<Checkbox />}
                value={jobState.isEntryJob}
                checked={jobState.isEntryJob}
                onChange={() => setJobState((prev) => ({ ...prev, isEntryJob: !prev.isEntryJob }))}
                label="Set as the entry point for the pipeline"
              />
            </FormGroup>
          </Grid>
        </Grid>
        <Grid container direction={"row"} spacing={2} sx={{ mb: "16px" }}>
          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <FormControl fullWidth>
              <InputLabel required>Connection</InputLabel>
              <Select
                value={jobState.location}
                label="Connection"
                required
                MenuProps={{ sx: { height: "220px" } }}
                onChange={(e) => handleConnectionChange(e.target.value)}
              >
                {locations.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.text}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <FormControl fullWidth>
              <InputLabel required>Type</InputLabel>
              <Select
                value={jobState.Type}
                label="Type"
                required
                MenuProps={{ sx: { height: "220px" } }}
                onChange={(e) => {
                  setPrevState(setJobState, { Type: e.target.value });
                }}
              >
                {Types.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.text}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        {jobState.location === "SNOWFLAKE" && (
          <Grid container direction={"row"} spacing={2} sx={{ mb: "16px" }}>
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <RunJobAsComponent
                participants={participantsExcludeVisitor}
                selectedUser={jobState.runJobAsUserId}
                handleChange={handleUserChange}
                handleRunUnderMyName={(e) =>
                  handleUserChange(e, { runJobAsUserId: user.User_id, runJobAsUserFullName: currentUserFullName })
                }
                error={Job_id && userNotExist}
                editable={accessRole === 1 && participantsExcludeVisitor.length > 0}
                nonEditableValue={userNotExist ? "" : jobState.runJobAsUserFullName}
                fullWidth
              />
            </Grid>
          </Grid>
        )}
        {locationType === "snowflake" && (
          <Grid container direction={"row"} spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <FormControl required fullWidth variant="outlined">
                <InputLabel>Compute Cluster</InputLabel>
                <Select
                  value={computeState.cluster}
                  onChange={(e) => setPrevState(setComputeState, { cluster: e.target.value })}
                  label="Compute Cluster"
                  MenuProps={{ sx: { height: "220px" } }}
                  error={!computeState.cluster}
                  errorText={"Cluster does not exists or you do not have access to work on it."}
                >
                  {connectionClusters?.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <KMKGroupSelect
                options={schemas}
                label="Schema"
                value={{ title: jobState.schema, groupBy: jobState.database }}
                handleChange={handleSchemaChange}
                error={schemaError}
                errorText={"Schema does not exists or you do not have access to work on it."}
                noOptionsText={"No schema found"}
                className={classes.AutoComplete}
                PaperComponent={CustomPaper}
                fullWidth
                required
              />
            </Grid>
          </Grid>
        )}
        <Grid
          size={{
            xs: 12,
            sm: 4,
          }}
        ></Grid>
        <Grid sx={{ paddingTop: "10px !important", display: "flex" }} size={12}>
          <Typography color={"#00000099"} padding={0} variant="subtitle2" component="span">
            {Job_id ? `Query * (Last updated by: ${jobState.queryLastUpdatedByName || "-"})` : `Query *`}
          </Typography>
        </Grid>
        <Grid sx={{ paddingTop: "2px !important" }} size={12}>
          <ComponentSuspense>
            <MyAceEditor
              fontSize={14}
              className={classes.queryArea}
              ref={queryRef}
              label="Query"
              value={jobState.query}
              setQuery={(value) => setPrevState(setJobState, { query: value })}
              location={jobState.location}
              type={locationType?.toLowerCase() || ""}
              runParams={{
                runJobAsUserId: Job_id && userNotExist ? null : jobState.runJobAsUserId,
                database: jobState.database,
                schema: jobState.schema,
                cluster: computeState.cluster,
              }}
              onError={(error) => setPrevState(setJobState, { queryError: error })}
              queryLastUpdatedByName={jobState.queryLastUpdatedByName}
            />
          </ComponentSuspense>
        </Grid>
      </Grid>
    );
  };

  const breadcrumbArr = [
    { link: "/my-workspaces", displayName: "Workspaces", active: false },
    {
      link: `/my-workspaces/${Workspace_id}`,
      displayName: workspaceName,
      active: false,
    },
    {
      link: `/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`,
      displayName: "Data Pipelines",
      active: false,
    },
    { link: "#", displayName: `${Job_id ? "Edit Routing Job" : "Add Routing Job"}`, active: true },
  ];

  const toggleSchedule = useCallback(
    () => setJobState((prev) => ({ ...prev, scheduleEnabled: !prev.scheduleEnabled })),
    []
  );

  return (
    <CommonLayout title="Job Scheduler" loading={jobState.loading} breadcrumbArr={breadcrumbArr}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "100vh",
          overflowX: "hidden",
        }}
        id="main_box"
      >
        <Backdrop
          sx={{ background: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={jobState.loading}
        >
          <CircularProgress color="primary" />
        </Backdrop>
        <Box maxWidth="lg" sx={{ mb: 4, mx: 0 }}>
          <Box className={classes.subContainer}>
            <Box className={classes.PanelContainer}>
              <Grid container direction="row" sx={{ mb: 4 }}>
                <Grid sx={{ mb: 1, display: "flex", alignItems: "center" }} size={12}>
                  <Typography className={classes.HeaderTitle} component="div">
                    {`${Job_id ? "Edit Routing Job" : "Add Routing Job"}`}
                  </Typography>
                  <ButtonUI
                    variant="contained"
                    color="success"
                    disabled={disableSaveBtn}
                    onClick={() => {
                      if (
                        accessRole === 2 &&
                        Job_id &&
                        compareQuery(prevJobState.query, jobState.query) &&
                        jobState.runJobAsUserId !== user.User_id
                      ) {
                        setPrevState(setJobReassignment, { openConfirm: true });
                      } else {
                        handleSave();
                      }
                    }}
                  >
                    Save
                  </ButtonUI>
                  <ButtonUI onClick={handleCancelClick} variant="contained" color="secondary" sx={{ ml: "15px" }}>
                    Cancel
                  </ButtonUI>

                  {Job_id ? (
                    <>
                      <ButtonUI
                        onClick={() => setOpenClone(true)}
                        variant="contained"
                        color="info"
                        sx={{ mx: "15px" }}
                        disabled={disableSaveBtn}
                      >
                        Clone
                      </ButtonUI>

                      <ButtonUI
                        variant="contained"
                        color="error"
                        onClick={() => {
                          setPrevState(setDeleteJobState, {
                            openConfirm: true,
                            confirmMessage: `Are you sure you want to delete "${jobState.jobName}" Job?"`,
                          });
                        }}
                        disabled={!Job_id}
                      >
                        Delete
                      </ButtonUI>
                    </>
                  ) : (
                    <></>
                  )}

                  <Box sx={{ display: "inline-flex", verticalAlign: "sub" }}>
                    <HelpContextIcon screen="Routing Job" />
                  </Box>
                </Grid>

                <Grid className={classes.BigTileContainer} size={12}>
                  <FormTile>{addJobForm()}</FormTile>
                </Grid>
                <Grid sx={{ paddingTop: "24px !important" }} size={12}>
                  <SmallToolbar>Schedule</SmallToolbar>
                  <FormTile
                    style={{
                      overflowX: "auto",
                    }}
                  >
                    <Scheduler
                      scheduleEnabled={jobState.scheduleEnabled}
                      cronValue={values.cronValue}
                      dispatchValues={dispatchValues}
                      toggleValue={toggleSchedule}
                      timeZone={jobState.timeZone}
                      nextRunTime={jobState.nextRunTime}
                    />
                  </FormTile>
                </Grid>
                <Grid sx={{ paddingTop: "24px !important" }} size={12}>
                  <SmallToolbar>Next Job</SmallToolbar>
                  <FormTile>
                    <NextJobForm
                      dataPipelineList={dataPipelineList}
                      showAlternateJob
                      nextJobToggle={nextJobState.nextJobToggle}
                      nextDataPipelineId={nextJobState.nextDataPipelineId}
                      nextJobId={nextJobState.nextJobId}
                      errorDataPipelineId={nextJobState.errorDataPipelineId}
                      errorJobId={nextJobState.errorJobId}
                      alternateDataPipelineId={nextJobState.alternateDataPipelineId}
                      alternateJobId={nextJobState.alternateJobId}
                      setNextJobState={setNextJobState}
                    />
                  </FormTile>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </Box>

      {openClone && (
        <CloneJob
          setOpenClone={setOpenClone}
          pipelineId={jobState.DataPipeline_id}
          handleSave={handleSave}
          JobType={JOB_BY_TYPE.routing}
        />
      )}

      {deleteJobState.openConfirm && (
        <ConfirmPopup
          configData={{
            popupTitle: "Delete Routing Job",
            popupMessage: deleteJobState.confirmMessage,
            handleCancel: () => handleCancel(setDeleteJobState, "openConfirm"),
            handleClick: deleteRoutingJob,
            loadingForConfirm: deleteJobState.loadingForConfirm,
          }}
        />
      )}
      {jobReassignment.openConfirm && (
        <ConfirmPopup
          configData={{
            popupTitle: "Job Reassignment",
            popupMessage: (
              <>
                <span>
                  You have edited the query for this job. Saving the job will now result in running the job under your
                  name.
                </span>
                <br />
                <span>Are you sure you want to proceed?</span>
              </>
            ),
            handleCancel: () => handleCancel(setJobReassignment, "openConfirm"),
            handleClick: handleSave,
          }}
        />
      )}
    </CommonLayout>
  );
}
